import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import CampaignIcon from "@mui/icons-material/Campaign";

const AccountsReceivableDialog = ({
  open,
  onClose,
  invoices,
  selectedInvoices,
  setSelectedInvoices,
  searchTerm,
  setSearchTerm,
  onEdit,
  onDelete,
  formatAmount,
  formatDate,
  selectedCompanyFilter,
  setSelectedCompanyFilter,
  loading,
  dialogMode = "blitzpay", // 'blitzpay' or 'project'
}) => {
  const totalAmount = invoices.reduce(
    (sum, inv) => sum + parseFloat(inv.amount_due || 0),
    0
  );

  // Calculate days late for an invoice
  const calculateDaysLate = (invoice) => {
    if (!invoice.created_at) return 0;

    const createdDate = new Date(invoice.created_at);
    const paymentTerm = invoice.payment_term || 30;
    const dueDate = new Date(createdDate);
    dueDate.setDate(dueDate.getDate() + paymentTerm);

    const today = new Date();
    const diffTime = today - dueDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : 0; // Only return positive days (overdue)
  };

  // Group invoices by company/client
  const groupedByEntity = invoices.reduce((acc, invoice) => {
    let entityKey, entityName, entityType;

    if (invoice.company_info) {
      entityKey = `company_${invoice.company_info.id}`;
      entityName = invoice.company_info.name;
      entityType = "company";
    } else if (invoice.client_name) {
      entityKey = `client_${invoice.client_name}`;
      entityName = invoice.client_name;
      entityType = "client";
    } else if (invoice.creator_info) {
      entityKey = `creator_${invoice.creator_info.creator_id}`;
      entityName = `Creator: ${invoice.creator_info.creator_id}`;
      entityType = "creator";
    } else {
      entityKey = "unknown";
      entityName = "Unknown";
      entityType = "unknown";
    }

    if (!acc[entityKey]) {
      acc[entityKey] = {
        name: entityName,
        type: entityType,
        invoices: [],
        totalAmount: 0,
        count: 0,
      };
    }

    acc[entityKey].invoices.push(invoice);
    acc[entityKey].totalAmount += parseFloat(invoice.amount_due || 0);
    acc[entityKey].count += 1;

    return acc;
  }, {});

  // Calculate average days late for each entity
  const entitiesWithMetrics = Object.entries(groupedByEntity)
    .map(([key, data]) => {
      const daysLateArray = data.invoices.map((inv) => calculateDaysLate(inv));
      const totalDaysLate = daysLateArray.reduce((sum, days) => sum + days, 0);
      const avgDaysLate =
        data.invoices.length > 0
          ? Math.round(totalDaysLate / data.invoices.length)
          : 0;
      const maxDaysLate = Math.max(...daysLateArray, 0);

      return {
        key,
        ...data,
        avgDaysLate,
        maxDaysLate,
      };
    })
    .sort((a, b) => b.totalAmount - a.totalAmount); // Sort by amount descending

  // Filter invoices based on selected company
  const displayedInvoices = selectedCompanyFilter
    ? invoices.filter((inv) => {
        if (inv.company_info)
          return `company_${inv.company_info.id}` === selectedCompanyFilter;
        if (inv.client_name)
          return `client_${inv.client_name}` === selectedCompanyFilter;
        if (inv.creator_info)
          return (
            `creator_${inv.creator_info.creator_id}` === selectedCompanyFilter
          );
        return selectedCompanyFilter === "unknown";
      })
    : invoices;

  const selectedEntity = selectedCompanyFilter
    ? entitiesWithMetrics.find((e) => e.key === selectedCompanyFilter)
    : null;

  // Dialog title based on mode
  const dialogTitle = dialogMode === "project" ? "Project Invoices (TCC)" : "BlitzPay Accounts Receivable";
  const dialogSubtitle = dialogMode === "project" 
    ? "TCC project invoices - Internal billing and project tracking"
    : selectedCompanyFilter
    ? `Detailed breakdown for ${selectedEntity?.name}`
    : "Outstanding BlitzPay invoices pending payment - Click a company card to drill down";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {dialogTitle}
              </Typography>
              {selectedCompanyFilter && dialogMode === "blitzpay" && (
                <Chip
                  label={`Viewing: ${selectedEntity?.name}`}
                  onDelete={() => setSelectedCompanyFilter(null)}
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {dialogMode === "project" && (
                <Chip
                  label="Project Invoices"
                  color="secondary"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {dialogSubtitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 2,
                mb: 3,
              }}
            >
              <Card sx={{ bgcolor: dialogMode === "project" ? "#f0fdf4" : "#f0f9ff", border: `1px solid ${dialogMode === "project" ? "#86efac" : "#bae6fd"}` }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Total Outstanding
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: dialogMode === "project" ? "#15803d" : "#0369a1", mt: 0.5 }}
                  >
                    {formatAmount(totalAmount)}
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ bgcolor: "#fef3c7", border: "1px solid #fde68a" }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Pending Approval
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#d97706", mt: 0.5 }}
                  >
                    {invoices.filter((inv) => inv.status === "pending").length}
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ bgcolor: "#dbeafe", border: "1px solid #93c5fd" }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Approved
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#2563eb", mt: 0.5 }}
                  >
                    {invoices.filter((inv) => inv.status === "approved").length}
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ bgcolor: "#fee2e2", border: "1px solid #fecaca" }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    {dialogMode === "project" ? "Total Invoices" : "Companies/Clients"}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#dc2626", mt: 0.5 }}
                  >
                    {dialogMode === "project" ? invoices.length : entitiesWithMetrics.length}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Company/Entity Breakdown - only show if not in project mode or not filtered */}
            {!selectedCompanyFilter && dialogMode === "blitzpay" && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Outstanding by Company/Client
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(280px, 1fr))",
                    gap: 2,
                  }}
                >
                  {entitiesWithMetrics.map((entity) => (
                    <Card
                      key={entity.key}
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                      }}
                      onClick={() => setSelectedCompanyFilter(entity.key)}
                    >
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            mb: 2,
                          }}
                        >
                          {entity.type === "company" ? (
                            <BusinessIcon
                              sx={{ color: "#3b82f6", fontSize: 24 }}
                            />
                          ) : (
                            <PersonIcon
                              sx={{ color: "#8b5cf6", fontSize: 24 }}
                            />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600, mb: 0.5 }}
                            >
                              {entity.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entity.type === "company"
                                ? "Company"
                                : entity.type === "creator"
                                ? "Creator"
                                : "Client"}
                            </Typography>
                          </Box>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Outstanding:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: "#059669" }}
                          >
                            {formatAmount(entity.totalAmount)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Invoices:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {entity.count}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Avg Days Late:
                          </Typography>
                          <Chip
                            label={
                              entity.avgDaysLate === 0
                                ? "On Time"
                                : `${entity.avgDaysLate} days`
                            }
                            size="small"
                            color={
                              entity.avgDaysLate === 0
                                ? "success"
                                : entity.avgDaysLate > 30
                                ? "error"
                                : "warning"
                            }
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                            }}
                          />
                        </Box>

                        {entity.maxDaysLate > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Max Days Late:
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "#dc2626" }}
                            >
                              {entity.maxDaysLate} days
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ mt: 2, textAlign: "center" }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#3b82f6", fontWeight: 600 }}
                          >
                            Click to view details →
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}

            {/* Back button when filtered and in BlitzPay mode */}
            {selectedCompanyFilter && dialogMode === "blitzpay" && (
              <Box sx={{ mb: 2 }}>
                <Button
                  startIcon={<WarningIcon />}
                  onClick={() => setSelectedCompanyFilter(null)}
                  variant="outlined"
                  size="small"
                >
                  Back to All Companies
                </Button>
              </Box>
            )}

            {/* Toolbar */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <TextField
                size="small"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 350 }}
              />

              {selectedInvoices.length > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={onDelete}
                >
                  Delete Selected ({selectedInvoices.length})
                </Button>
              )}
            </Box>

            {/* Invoices Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f9fafb" }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          selectedInvoices.length ===
                            displayedInvoices.length &&
                          displayedInvoices.length > 0
                        }
                        indeterminate={
                          selectedInvoices.length > 0 &&
                          selectedInvoices.length < displayedInvoices.length
                        }
                        onChange={(e) =>
                          setSelectedInvoices(
                            e.target.checked
                              ? displayedInvoices.map((i) => i.id)
                              : []
                          )
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Invoice Details
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Client/Company
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Campaign</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Days Late</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                        <Typography color="text.secondary">
                          No outstanding invoices
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedInvoices.map((invoice) => {
                      const daysLate = calculateDaysLate(invoice);
                      const paymentTerm = invoice.payment_term || 30;
                      const createdDate = invoice.created_at
                        ? new Date(invoice.created_at)
                        : null;
                      const dueDate = createdDate
                        ? new Date(
                            createdDate.getTime() +
                              paymentTerm * 24 * 60 * 60 * 1000
                          )
                        : null;

                      return (
                        <TableRow
                          key={invoice.id}
                          hover
                          sx={{ "&:hover": { bgcolor: "#f9fafb" } }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedInvoices.includes(invoice.id)}
                              onChange={() =>
                                setSelectedInvoices((prev) =>
                                  prev.includes(invoice.id)
                                    ? prev.filter((id) => id !== invoice.id)
                                    : [...prev, invoice.id]
                                )
                              }
                            />
                          </TableCell>

                          {/* Invoice Details */}
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <DescriptionIcon
                                sx={{ color: "#6b7280", fontSize: 20 }}
                              />
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {invoice.po_number || `#${invoice.id}`}
                                </Typography>
                                {invoice.invoice_type && (
                                  <Chip
                                    label={invoice.invoice_type}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      mt: 0.5,
                                      height: 20,
                                      fontSize: "0.7rem",
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                            {invoice.supporting_document && (
                              <Tooltip title="Has supporting document">
                                <AttachFileIcon
                                  sx={{
                                    color: "#6b7280",
                                    fontSize: 16,
                                    ml: 1,
                                  }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>

                          {/* Client/Company */}
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                              }}
                            >
                              {invoice.company_info ? (
                                <>
                                  <BusinessIcon
                                    sx={{
                                      color: "#6b7280",
                                      fontSize: 20,
                                      mt: 0.5,
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {invoice.company_info.name}
                                    </Typography>
                                    {invoice.user_info && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {invoice.user_info.first_name}{" "}
                                        {invoice.user_info.last_name}
                                      </Typography>
                                    )}
                                  </Box>
                                </>
                              ) : invoice.client_name ? (
                                <>
                                  <PersonIcon
                                    sx={{
                                      color: "#6b7280",
                                      fontSize: 20,
                                      mt: 0.5,
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      {invoice.client_name}
                                    </Typography>
                                    {invoice.client_email && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {invoice.client_email}
                                      </Typography>
                                    )}
                                  </Box>
                                </>
                              ) : invoice.creator_info ? (
                                <>
                                  <PersonIcon
                                    sx={{
                                      color: "#6b7280",
                                      fontSize: 20,
                                      mt: 0.5,
                                    }}
                                  />
                                  <Typography variant="body2">
                                    Creator: {invoice.creator_info.creator_id}
                                  </Typography>
                                </>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  N/A
                                </Typography>
                              )}
                            </Box>
                          </TableCell>

                          {/* Campaign */}
                          <TableCell>
                            {invoice.campaign_name ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <CampaignIcon
                                  sx={{ color: "#6b7280", fontSize: 18 }}
                                />
                                <Typography
                                  variant="body2"
                                  noWrap
                                  sx={{ maxWidth: 150 }}
                                >
                                  {invoice.campaign_name}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No campaign
                              </Typography>
                            )}
                          </TableCell>

                          {/* Amount */}
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: "#059669" }}
                            >
                              {formatAmount(invoice.amount_due)}
                            </Typography>
                            {invoice.platform_fee &&
                              parseFloat(invoice.platform_fee) > 0 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Fee: {formatAmount(invoice.platform_fee)}
                                </Typography>
                              )}
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            <Chip
                              label={invoice.status}
                              size="small"
                              color={
                                invoice.status === "approved"
                                  ? "info"
                                  : "warning"
                              }
                              sx={{ fontWeight: 500, minWidth: 80 }}
                            />
                            {invoice.approval_info && (
                              <Tooltip
                                title={`Approved by ${invoice.approval_info.approved_by_name}`}
                              >
                                <CheckCircleIcon
                                  sx={{
                                    color: "#10b981",
                                    fontSize: 16,
                                    ml: 0.5,
                                  }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>

                          {/* Due Date */}
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {dueDate ? formatDate(dueDate) : "N/A"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Net {paymentTerm}
                            </Typography>
                          </TableCell>

                          {/* Days Late */}
                          <TableCell>
                            {daysLate === 0 ? (
                              <Chip
                                label="On Time"
                                size="small"
                                color="success"
                                sx={{ fontWeight: 600 }}
                              />
                            ) : (
                              <Chip
                                label={`${daysLate} days`}
                                size="small"
                                color={daysLate > 30 ? "error" : "warning"}
                                sx={{ fontWeight: 600 }}
                                icon={<WarningIcon />}
                              />
                            )}
                          </TableCell>

                          {/* Actions */}
                          <TableCell align="right">
                            <Tooltip title="Edit Invoice">
                              <IconButton
                                size="small"
                                onClick={() => onEdit(invoice)}
                                sx={{ color: "#3b82f6" }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary stats when filtered by company */}
            {selectedCompanyFilter && selectedEntity && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: dialogMode === "project" ? "#f0fdf4" : "#f0f9ff",
                  borderRadius: 2,
                  border: `1px solid ${dialogMode === "project" ? "#86efac" : "#bae6fd"}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: dialogMode === "project" ? "#15803d" : "#0369a1", mb: 1 }}
                >
                  {selectedEntity.name} Summary:
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Outstanding
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {formatAmount(selectedEntity.totalAmount)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Number of Invoices
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {selectedEntity.count}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Average Days Late
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 700,
                        color:
                          selectedEntity.avgDaysLate > 30
                            ? "#dc2626"
                            : selectedEntity.avgDaysLate > 0
                            ? "#f59e0b"
                            : "#059669",
                      }}
                    >
                      {selectedEntity.avgDaysLate} days
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Most Overdue
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "#dc2626" }}
                    >
                      {selectedEntity.maxDaysLate} days
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Notes Section */}
            {displayedInvoices.some((inv) => inv.notes || inv.client_notes) && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "#f9fafb", borderRadius: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "#6b7280" }}
                >
                  Note: Some invoices contain additional notes. Click edit to
                  view details.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountsReceivableDialog;